import React, { useState, useEffect } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Drawer,
  Checkbox,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "notistack";
import { useAppDispatch, useAppSelector } from "../../../../../Config/Hooks";
import { RootState } from "../../../../../Config/Store";
import { fetchFiltersData } from "./SideBarFilterReducer";
import { LoadingButton } from "@mui/lab";
import {ACard, ADataGridFilter, ASectionTitle} from "@atiautomacao/ati-ui-library";
import { FilterAltOffOutlined } from "@mui/icons-material";
import { MenuProps } from "../../../../../Utils/PropsStyles";
import { FilterState, SITUATION_OPTIONS, getInitialFilterState } from "../ChipFilterAlarms/types";

interface SideBarProps {
  open: boolean;
  onClose: () => void;
  onApplyFilters: (filters: Array<ADataGridFilter>, count: number) => void;
  hasClearFilters: boolean;
  /** Chave do localStorage de onde o estado é hidratado. Padrão: filtros da tela de Alarmes. */
  storageKey?: string;
  subtitle?: string;
}


const SideBar: React.FC<SideBarProps> = ({
  open,
  onClose,
  onApplyFilters,
  hasClearFilters,
  storageKey,
  subtitle = "Selecione os filtros para a tela de Alarmes",
}) => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const [filters, setFilters] = useState<FilterState>(() => getInitialFilterState(storageKey));

  const { equipments, severities, loading, errorMessage } = useAppSelector(
    (state: RootState) => state.sideBarFilter
  );

  useEffect(() => {
    if (open) {
      dispatch(fetchFiltersData());
      setFilters(getInitialFilterState(storageKey));
    }
  }, [open, dispatch, storageKey]);

  useEffect(() => {
    if (errorMessage) {
      enqueueSnackbar(errorMessage, { variant: "error" });
    }
  }, [errorMessage, enqueueSnackbar]);

  const handleFilterChange = (
    filterType: keyof FilterState,
    value: string | string[]
  ) => {

    if (!value) return;
    
    const validate = Array.isArray(value) ? value : [value];
    if (validate.some(v => !v || typeof v !== 'string')) return;
    
    setFilters({
      ...filters,
      [filterType]: Array.isArray(value) ? value : [value],
    });
  };

  const handleClearFilters = () => {
    setFilters({
      equipmentTypeIds: [],
      severityName: [],
      situationNames: [],
    });
    onApplyFilters([] as ADataGridFilter[], 0);
  };

  useEffect(() => {
    if (hasClearFilters) {
      handleClearFilters();
    }
  }, [hasClearFilters]);

  const handleApplyFilters = () => {
    const appliedFilters: Array<ADataGridFilter> = [];
    let filtersCount = 0;
    
    // Consolidar múltiplos equipmentTypeIds com separador ~ em um único filter
    if (filters.equipmentTypeIds.length > 0) {
      appliedFilters.push({
        name: "equipmentTypeId",
        condition: "equals",
        value: filters.equipmentTypeIds.join("~"),
        sort: "none"
      });
      filtersCount+= filters.equipmentTypeIds.length;
    }
    
    if (filters.severityName.length > 0) {
      appliedFilters.push({
        name: "severityName",
        condition: "contains",
        value: filters.severityName.join("~"),
        sort: "none"
      });
      filtersCount+= filters.severityName.length;
    }

    if (filters.situationNames.length > 0) {
      appliedFilters.push({
        name: 'situation',
        condition: "equals",
        value:  filters.situationNames.join('~'),
        sort: "none"
      });
      filtersCount+= filters.situationNames.length;
    }
    onApplyFilters(appliedFilters, filtersCount);
    onClose();
  };

  return (
    <Drawer 
      open={open}
      anchor="right"
      variant="temporary"
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 2, // força ficar acima da topbar
      }}
      PaperProps={{ sx: { width: '428px', mt: '60px' } }}
      onClose={onClose} aria-labelledby="drawer-sidebar-title"
      aria-describedby="sidebar-for-filtering-alarms"
    >      
      <ACard
        styleProps={{
          cardStyle: { paddingTop: 0, borderRadius: 0},
          headerStyle: { padding: 0 },
          subTitleStyle: { paddingTop: '0px' },
          contentStyle: { padding: 0, height: 'calc(90% - 164px)'},
        }}
        footerActions={
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: 2, mb: 2, padding: '0 24px' }}>
            <LoadingButton
              variant="contained"
              color="primary"
              loading={loading}
              onClick={handleApplyFilters}
              disabled={loading}
            >
              Filtrar
            </LoadingButton>
            <LoadingButton variant="outlined" color="secondary" onClick={onClose} loading={loading}>
              Voltar
            </LoadingButton>
          </Box>
        }
      >
        <Box sx={{padding: '16px'}}>
        <ASectionTitle
          titleLabel={"Filtros Avançados"}
          subTitleLabel={subtitle}
          styleContainer={{paddingTop: window.innerWidth < 768 ? 'calc(20% - 64px)' : 'calc(10% - 64px)' }}
          contentChildren={
              <IconButton onClick={onClose} size="small">
                  <CloseIcon />
              </IconButton>
          }
          />
      </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2, px: 3, pb: 2  }}>
          <FormControl fullWidth size="small">
            <InputLabel id="equipment-label">Equipamento</InputLabel>
            <Select
              labelId="equipment-label"
              fullWidth
              multiple
              MenuProps={{ ...MenuProps, disablePortal: true }}
              value={filters.equipmentTypeIds}
              onChange={(e) => handleFilterChange("equipmentTypeIds", e.target.value)}
              label="Equipamento"
              disabled={loading}
              renderValue={(selected) => 
                `${selected.length} selecionado${selected.length !== 1 ? 's' : ''}`
              }
            >
              {[...equipments].sort((a, b) => a.sortPriority - b.sortPriority).map((equip) => (
                <MenuItem key={equip.id} value={equip.id.toString()}>
                  <Checkbox 
                    checked={filters.equipmentTypeIds.includes(equip.id.toString())}
                    disabled={loading}
                    size="small"
                  />
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', ml: 1 }}>
                    {equip.displayName}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel id="severity-label">Severidade</InputLabel>
            <Select
              labelId="severity-label"
              fullWidth
              multiple
              MenuProps={{ ...MenuProps, disablePortal: true }}
              value={filters.severityName}
              onChange={(e) => handleFilterChange("severityName", e.target.value)}
              label="Severidade"
              disabled={loading}
              renderValue={(selected) => 
                `${selected.length} selecionado${selected.length !== 1 ? 's' : ''}`
              }
            >
              {[...severities].sort((a, b) => a.sortPriority - b.sortPriority).map((sev) => (
                <MenuItem key={sev.name} value={sev.name}>
                  <Checkbox 
                    checked={filters.severityName.includes(sev.name)}
                    disabled={loading}
                    size="small"
                  />
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', ml: 1 }}>
                    {sev.displayName || sev.name}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel id="situation-label">Situação</InputLabel>
            <Select
              labelId="situation-label"
              fullWidth
              multiple
              MenuProps={{ ...MenuProps, disablePortal: true }}
              value={filters.situationNames}
              onChange={(e) => handleFilterChange("situationNames", e.target.value)}
              label="Situação"
              renderValue={(selected) => 
                `${selected.length} selecionado${selected.length !== 1 ? 's' : ''}`
              }
            >
              {SITUATION_OPTIONS.map((option) => (
                <MenuItem key={option.name} value={option.name}>
                  <Checkbox 
                    checked={filters.situationNames.includes(option.name)}
                    disabled={loading}
                    size="small"
                  />
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', ml: 1 }}>
                    {option.displayName}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <LoadingButton
              variant="text"
              sx={{fontSynthesisWeight: 500}}
              startIcon={<FilterAltOffOutlined />}
              color="primary"
              loading={loading}
              onClick={handleClearFilters}
              disabled={loading}
            >
              Limpar Filtros
            </LoadingButton>
          </Box>

        </Box>

      </ACard>
    </Drawer>
  );
};

export default SideBar;
